/**
 * Business Intelligence Service
 * Queries operational + billing data for dashboard insights
 */

import { supabase } from '../supabaseClient';
import { parseDbTime, parseWindowTime } from '../utils/dbTime';   // __TZ_WINDOWS__

/**
 * Get date range bounds
 */
const getDateRange = (days) => {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - days);
  return { start: start.toISOString(), end: end.toISOString() };
};

/**
 * Core KPIs — overall business metrics
 */
export const getKPIs = async (days = 30) => {
  const { start, end } = getDateRange(days);

  // Loads in period
  const { data: loads } = await supabase
    .from('loads')
    .select('id, status, revenue_amount, created_at, driver_id, customer_id, billed_at')
    .gte('created_at', start)
    .lte('created_at', end);

  const allLoads = loads || [];
  const completed = allLoads.filter(l => l.status === 'complete' || l.status === 'billed' || l.status === 'ready_to_bill');
  const totalRevenue = allLoads.reduce((sum, l) => sum + (parseFloat(l.revenue_amount) || 0), 0);
  const billedRevenue = allLoads.filter(l => l.billed_at).reduce((sum, l) => sum + (parseFloat(l.revenue_amount) || 0), 0);
  const unbilledRevenue = totalRevenue - billedRevenue;

  // Active drivers
  const uniqueDrivers = new Set(allLoads.filter(l => l.driver_id).map(l => l.driver_id));

  // Avg loads per day
  const daysInPeriod = Math.max(1, days);
  const avgLoadsPerDay = (allLoads.length / daysInPeriod).toFixed(1);

  // Avg revenue per load
  const avgRevenuePerLoad = completed.length > 0 ? (totalRevenue / completed.length).toFixed(2) : '0.00';

  return {
    totalLoads: allLoads.length,
    completedLoads: completed.length,
    pendingLoads: allLoads.filter(l => l.status === 'pending').length,
    activeLoads: allLoads.filter(l => !['complete', 'billed', 'ready_to_bill', 'archived', 'pending'].includes(l.status)).length,
    totalRevenue,
    billedRevenue,
    unbilledRevenue,
    activeDrivers: uniqueDrivers.size,
    avgLoadsPerDay: parseFloat(avgLoadsPerDay),
    avgRevenuePerLoad: parseFloat(avgRevenuePerLoad),
    completionRate: allLoads.length > 0 ? ((completed.length / allLoads.length) * 100).toFixed(1) : '0',
  };
};

/**
 * Revenue breakdown by customer
 */
export const getRevenueByCustomer = async (days = 30) => {
  const { start } = getDateRange(days);

  const { data } = await supabase
    .from('loads')
    .select('revenue_amount, customer:customers(company_name)')
    .gte('created_at', start)
    .not('revenue_amount', 'is', null);

  if (!data) return [];

  const byCustomer = {};
  data.forEach(l => {
    const name = l.customer?.company_name || 'Unknown';
    if (!byCustomer[name]) byCustomer[name] = { name, revenue: 0, loads: 0 };
    byCustomer[name].revenue += parseFloat(l.revenue_amount) || 0;
    byCustomer[name].loads += 1;
  });

  return Object.values(byCustomer).sort((a, b) => b.revenue - a.revenue);
};

/**
 * Revenue breakdown by driver
 */
export const getRevenueByDriver = async (days = 30) => {
  const { start } = getDateRange(days);

  const { data } = await supabase
    .from('loads')
    .select('revenue_amount, status, driver:drivers(name)')
    .gte('created_at', start)
    .not('driver_id', 'is', null);

  if (!data) return [];

  const byDriver = {};
  data.forEach(l => {
    const name = l.driver?.name || 'Unassigned';
    if (!byDriver[name]) byDriver[name] = { name, revenue: 0, loads: 0, completed: 0 };
    byDriver[name].revenue += parseFloat(l.revenue_amount) || 0;
    byDriver[name].loads += 1;
    if (['complete', 'billed', 'ready_to_bill'].includes(l.status)) byDriver[name].completed += 1;
  });

  return Object.values(byDriver).sort((a, b) => b.revenue - a.revenue);
};

/**
 * Daily load volume + revenue trend
 */
export const getDailyTrend = async (days = 30) => {
  const { start } = getDateRange(days);

  const { data } = await supabase
    .from('loads')
    .select('created_at, revenue_amount, status')
    .gte('created_at', start)
    .order('created_at', { ascending: true });

  if (!data) return [];

  const byDate = {};
  data.forEach(l => {
    const date = l.created_at.split('T')[0];
    if (!byDate[date]) byDate[date] = { date, loads: 0, revenue: 0, completed: 0 };
    byDate[date].loads += 1;
    byDate[date].revenue += parseFloat(l.revenue_amount) || 0;
    if (['complete', 'billed', 'ready_to_bill'].includes(l.status)) byDate[date].completed += 1;
  });

  return Object.values(byDate);
};

/**
 * On-time delivery performance
 */
export const getOnTimePerformance = async (days = 30) => {
  const { start } = getDateRange(days);

  const { data } = await supabase
    .from('load_stops')
    .select('time_window_start, time_window_end, actual_arrival, stop_type, load:loads(created_at, driver_id, driver:drivers(name))')
    .gte('load.created_at', start)
    .eq('stop_type', 'delivery')
    .not('actual_arrival', 'is', null)
    .not('time_window_end', 'is', null);

  if (!data || data.length === 0) return { onTimeRate: 0, total: 0, onTime: 0, late: 0, byDriver: {} };

  let onTime = 0;
  let late = 0;
  const byDriver = {};

  data.forEach(stop => {
    // __TZ_WINDOWS__ not the same kind: actual_arrival is naive UTC, time_window_end is
    // Toronto wall clock (as the hub writes it). Each through its own reader.
    const arrival = parseDbTime(stop.actual_arrival);
    const windowEnd = parseWindowTime(stop.time_window_end);
    const isOnTime = arrival <= windowEnd;
    const driverName = stop.load?.driver?.name || 'Unknown';

    if (isOnTime) onTime++;
    else late++;

    if (!byDriver[driverName]) byDriver[driverName] = { onTime: 0, late: 0, total: 0 };
    byDriver[driverName].total += 1;
    if (isOnTime) byDriver[driverName].onTime += 1;
    else byDriver[driverName].late += 1;
  });

  return {
    onTimeRate: data.length > 0 ? ((onTime / data.length) * 100).toFixed(1) : 0,
    total: data.length,
    onTime,
    late,
    byDriver,
  };
};

/**
 * Billing efficiency — distance charges, fuel costs
 */
export const getBillingMetrics = async (days = 30) => {
  const { start } = getDateRange(days);

  const { data } = await supabase
    .from('billing_distance_records')
    .select('calculated_distance_meters, actual_distance_meters, fuel_consumed_liters, total_distance_charge, total_time_charge, billing_rate_per_km')
    .gte('created_at', start);

  if (!data || data.length === 0) return {
    totalDistanceKm: 0, totalFuelLiters: 0, totalDistanceCharges: 0,
    totalTimeCharges: 0, avgRatePerKm: 0, fuelEfficiency: 0, records: 0,
  };

  const totals = data.reduce((acc, r) => ({
    distanceM: acc.distanceM + (r.actual_distance_meters || r.calculated_distance_meters || 0),
    fuel: acc.fuel + (parseFloat(r.fuel_consumed_liters) || 0),
    distCharge: acc.distCharge + (parseFloat(r.total_distance_charge) || 0),
    timeCharge: acc.timeCharge + (parseFloat(r.total_time_charge) || 0),
    rateSum: acc.rateSum + (parseFloat(r.billing_rate_per_km) || 0),
  }), { distanceM: 0, fuel: 0, distCharge: 0, timeCharge: 0, rateSum: 0 });

  const distKm = totals.distanceM / 1000;

  return {
    totalDistanceKm: Math.round(distKm),
    totalFuelLiters: Math.round(totals.fuel * 10) / 10,
    totalDistanceCharges: Math.round(totals.distCharge * 100) / 100,
    totalTimeCharges: Math.round(totals.timeCharge * 100) / 100,
    avgRatePerKm: data.length > 0 ? (totals.rateSum / data.length).toFixed(2) : 0,
    fuelEfficiency: totals.fuel > 0 ? (distKm / totals.fuel).toFixed(1) : 0, // km per liter
    records: data.length,
  };
};

/**
 * Driver performance scorecard
 */
export const getDriverScorecard = async (days = 30) => {
  const { start } = getDateRange(days);

  const { data: loads } = await supabase
    .from('loads')
    .select('id, status, revenue_amount, driver_id, created_at, driver:drivers(name)')
    .gte('created_at', start)
    .not('driver_id', 'is', null);

  const { data: breaks } = await supabase
    .from('driver_breaks')
    .select('driver_id, expected_duration_minutes, started_at, ended_at, status')
    .gte('started_at', start);

  const { data: billing } = await supabase
    .from('billing_distance_records')
    .select('driver_id, actual_distance_meters, fuel_consumed_liters, total_distance_charge, total_time_charge')
    .gte('created_at', start);

  const scorecard = {};

  (loads || []).forEach(l => {
    const name = l.driver?.name || 'Unknown';
    const id = l.driver_id;
    if (!scorecard[id]) scorecard[id] = { name, loads: 0, completed: 0, revenue: 0, distanceKm: 0, fuelL: 0, breakMinutes: 0, charges: 0 };
    scorecard[id].loads += 1;
    scorecard[id].revenue += parseFloat(l.revenue_amount) || 0;
    if (['complete', 'billed', 'ready_to_bill'].includes(l.status)) scorecard[id].completed += 1;
  });

  (billing || []).forEach(b => {
    if (scorecard[b.driver_id]) {
      scorecard[b.driver_id].distanceKm += (b.actual_distance_meters || 0) / 1000;
      scorecard[b.driver_id].fuelL += parseFloat(b.fuel_consumed_liters) || 0;
      scorecard[b.driver_id].charges += (parseFloat(b.total_distance_charge) || 0) + (parseFloat(b.total_time_charge) || 0);
    }
  });

  (breaks || []).forEach(b => {
    if (scorecard[b.driver_id] && b.ended_at) {
      const mins = (new Date(b.ended_at) - new Date(b.started_at)) / 60000;
      scorecard[b.driver_id].breakMinutes += mins;
    }
  });

  return Object.values(scorecard).map(d => ({
    ...d,
    distanceKm: Math.round(d.distanceKm),
    fuelL: Math.round(d.fuelL * 10) / 10,
    breakMinutes: Math.round(d.breakMinutes),
    avgRevenuePerLoad: d.completed > 0 ? (d.revenue / d.completed).toFixed(2) : '0.00',
    completionRate: d.loads > 0 ? ((d.completed / d.loads) * 100).toFixed(0) : '0',
  })).sort((a, b) => b.revenue - a.revenue);
};

export default {
  getKPIs,
  getRevenueByCustomer,
  getRevenueByDriver,
  getDailyTrend,
  getOnTimePerformance,
  getBillingMetrics,
  getDriverScorecard,
};
